import type { OnOAuthSuccessCallback } from '@atlassian/rovo-connectors-config/hooks/useRovoConfig';
import type {
	TProductDataRegions as ProductDataRegions,
	TProductEdition as ProductEdition,
	TRovoFeatures as RovoFeatures,
} from '@atlassian/search-client/fetch-search-config';
import type { SmartlinkUsageAndTopApps } from '@atlassian/search-client/smartlink-usage-and-top-apps';
import type { ExperimentConfig, SideBySideExperiment } from '@atlassian/search-experiment/types';

import type { NounKeys } from './nouns';
import type { PrimaryProductKey, ProductKeys } from './products';
import type { ThirdPartyConfigsBootstrap } from './schemas/3p-config';
import type { Overrides, SearchQueryStringParams } from './schemas/query-params';
import type { onInputDropdownResultSelect, onInputSubmit, UserDetailsWithPicture } from './types';

/**
 * This state object contains all the configuration which must be loaded to make the page functional.
 */
export const initialState: {
	products: never[];
	nouns: never[];
	allFilterExcludedProducts: never[];
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	searchSessionId: string;
	queryParams: {};
	overrides?: Overrides;
	prevQueryParams: {};
	initialSpaceARIs: never[];
	experimentConfig: {
		backendExperiment: undefined;
		frontendExperiments: undefined;
		dynamicExperiments: undefined;
	};
	experimentLayers: never[];
	isRovoEnabled: false;
	isConfigLoaded: false;
	rovoFeatures: {};
	isJiraIssueIndexed: false;
	productEdition: undefined;
	productDataRegions: undefined;
	linkedCloudARIs: undefined;
	analyticsAttributes: undefined;
	aggAbsoluteUrl: undefined;
	cloudId: undefined;
	aggregatorHostName: undefined;
	SAINHostName: undefined;
	user: undefined;
	primaryProduct: undefined;
	primarySubProduct: undefined;
	orgId: undefined;
	intercomHmac: undefined;
	referrer: undefined;
	initialTextQueryParam: undefined;
	features: undefined;
	hasEditOrViewPlanPermission: false;
	onSearchCompleted: undefined;
	onOAuthSuccess: undefined;
	smartLinkUsage: undefined;
	smartLinkUsageAndTopApps: undefined;
} = {
	products: [],
	nouns: [],
	allFilterExcludedProducts: [],
	thirdPartyConfigs: {} as ThirdPartyConfigsBootstrap,
	searchSessionId: '',
	queryParams: {},
	prevQueryParams: {},
	overrides: undefined,
	initialSpaceARIs: [],
	experimentConfig: {
		backendExperiment: undefined,
		frontendExperiments: undefined,
		dynamicExperiments: undefined,
	},
	experimentLayers: [],
	isRovoEnabled: false,
	isConfigLoaded: false,
	rovoFeatures: {},
	isJiraIssueIndexed: false,
	productEdition: undefined,
	productDataRegions: undefined,
	linkedCloudARIs: undefined,
	analyticsAttributes: undefined,
	aggAbsoluteUrl: undefined,
	cloudId: undefined,
	aggregatorHostName: undefined,
	SAINHostName: undefined,
	user: undefined,
	primaryProduct: undefined,
	primarySubProduct: undefined,
	orgId: undefined,
	intercomHmac: undefined,
	referrer: undefined,
	initialTextQueryParam: undefined,
	features: undefined,
	hasEditOrViewPlanPermission: false,
	onSearchCompleted: undefined,
	onOAuthSuccess: undefined,
	smartLinkUsage: undefined,
	smartLinkUsageAndTopApps: undefined,
} satisfies BootstrapState;

export type Features = {
	sideBySide?: {
		/**
		 * Whether the side-by-side layout is enabled.
		 */
		enabled: boolean;
		/**
		 * Experiments specifying the treatments to be implemented.
		 */
		experiments?: SideBySideExperiment[];
	};
	chromeExtension?: {
		enabled: boolean;
		onInputDropdownResultSelect: onInputDropdownResultSelect;
		onInputSubmit: onInputSubmit;
	};
	lite?: {
		/**
		 * Whether the lite layout is enabled.
		 * When enabled, the search page renders without the right panel,
		 * with a simplified header (product filter tabs only).
		 */
		enabled: boolean;
	};
};

export type BootstrapState = {
	aggAbsoluteUrl: string | undefined;
	cloudId: string | undefined;
	products: ProductKeys[];
	nouns: NounKeys[];
	aggregatorHostName: string | undefined;
	allFilterExcludedProducts: ProductKeys[];
	SAINHostName: string | undefined;
	user: UserDetailsWithPicture | undefined;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	primaryProduct: PrimaryProductKey | undefined;
	primarySubProduct: string | undefined;
	searchSessionId: string;
	orgId: string | undefined;
	analyticsAttributes: Record<string, any> | undefined;
	queryParams: SearchQueryStringParams;
	prevQueryParams: SearchQueryStringParams;
	overrides?: Overrides | undefined;
	/**
	 * The ARIs for the spaces parsed from query params.
	 * This was added to support the query param as a space key
	 * while most backend systems require ARIs to operate.
	 **/
	/**
	 * DO NOT CHANGE FROM [key: type | undefined] to [key?: type]
	 */
	initialSpaceARIs: string[];
	features: Features | undefined;
	experimentConfig: ExperimentConfig;
	experimentLayers: string[];
	intercomHmac: string | undefined;
	isRovoEnabled: boolean;
	isJiraIssueIndexed: boolean;
	isConfigLoaded: boolean;
	referrer: string | undefined;
	productEdition: ProductEdition | undefined;
	productDataRegions: ProductDataRegions | undefined;
	rovoFeatures: RovoFeatures | undefined;
	initialTextQueryParam: string | undefined;
	linkedCloudARIs: string[] | undefined;
	hasEditOrViewPlanPermission?: boolean;
	/** Callback invoked when Quick Find search completes successfully. */
	onSearchCompleted: OnSearchCompletedCallback | undefined;
	/** Callback invoked when a 3P connector has just completed OAuth locally. */
	onOAuthSuccess: OnOAuthSuccessCallback | undefined;
	/** Smart Link usage data per connector, keyed by product key. Value is usage %. */
	smartLinkUsage: Record<string, number> | undefined;
	/**
	 * Tenant-wide Smart Link usage percentage plus ranked list of most used apps.
	 * Sourced from the TAP site trait `rovo_nudge_end_user_rendered_smartlinks_percent_and_most_used_apps`.
	 */
	smartLinkUsageAndTopApps: SmartlinkUsageAndTopApps | undefined;
};

/**
 * Internal type used for the setQueryParams function used within FPS
 */
export type SetQueryParamsCallback = (newParams: Partial<SearchQueryStringParams>) => void;

/**
 * Internal type used for the refreshSearchSessionId function used within QF
 */
export type RefreshSearchSessionIdCallback = () => void;

/**
 * Internal type used for the clearReferrer function used when FPS mounts
 */
export type ClearReferrerCallback = () => void;

/**
 * Internal type used for the setThirdPartyConfigs function used within OAuth workflow
 */
export type SetThirdPartyConfigsCallback = (configs: Partial<ThirdPartyConfigsBootstrap>) => void;

/**
 * Internal type used for the onSearchCompleted callback which is invoked when a search completes successfully, and can be used to trigger side effects such as analytics or refreshing data
 */
export type OnSearchCompletedCallback = (resultCount?: number, query?: string) => void;
